import { Link } from "react-router-dom";
import { DoorOpen, UserCheck, MoonStar, ShieldAlert, Siren, Home, HeartHandshake, Syringe, ExternalLink, Thermometer } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import ProtectedDownload from "@/components/ProtectedDownload";
import { Reveal } from "@/components/Reveal";
import { FlowerDoodle, WavyUnderline, Ladybug } from "@/components/Doodles";

const OCFS_SEARCH = "https://hs.ocfs.ny.gov/DCFS";
const OCFS_REGS = "https://ocfs.ny.gov/programs/childcare/regulations/";

const HIGHLIGHTS = [
    { icon: DoorOpen, title: "Open door policy", text: "Visit your child any time, no appointment needed." },
    { icon: UserCheck, title: "Secure pick-up", text: "Children go home only with adults you list. Anyone new shows photo ID." },
    { icon: MoonStar, title: "Safe sleep", text: "Infants always sleep on their backs in safety-approved cribs with nothing loose inside." },
    { icon: ShieldAlert, title: "Allergy-aware", text: "We follow New York's Elijah's Law with a written plan for every child with allergies." },
    {
        icon: Siren,
        title: "Emergency Ready",
        text: "We maintain a written emergency plan, conduct required emergency and fire drills, and have a designated relocation site to help ensure we are prepared in the event of an emergency.",
    },
    {
        icon: Home,
        title: "Healthy & Safe Home",
        text: "Our program maintains a smoke- and vape-free environment. Firearms are not present, pets are kept separate from children during program hours, and the hot tub is inaccessible to children, secured with a hard locking cover, and is never used as part of the childcare program.",
    },
    {
        icon: HeartHandshake,
        title: "Positive Guidance",
        text: "We guide children with kindness, patience, positive reinforcement, redirection, and age-appropriate expectations. Our approach focuses on helping children understand emotions, develop self-regulation, solve problems, and make positive choices.",
    },
    { icon: Syringe, title: "Immunizations", text: "Children follow NYS immunization requirements, which keeps every child in the group safer." },
];

export default function Safety() {
    usePageTitle("Safety & NYS OCFS Licensing | Little Seeds Flourish Massapequa Park");

    return (
        <div>
            <PageHero
                eyebrow="Health, safety & licensing"
                title={<>Your Child's Safety Comes First. Always.</>}
                sub="Calm, confident, and by the book — here's exactly how we keep every child safe."
                doodle="bee"
            />

            {/* Licensing badge */}
            <section className="pb-14 md:pb-20">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <div
                            className="relative bg-white border-2 border-sage/30 rounded-[2rem] p-8 md:p-10 text-center shadow-sm overflow-hidden"
                            data-testid="safety-license-badge"
                        >
                            <Ladybug className="absolute top-5 right-6 hidden sm:block" />
                            <span className="inline-flex p-4 rounded-3xl bg-sage/15 mb-5">
                                <ShieldCheckBig />
                            </span>
                            <h2 className="font-display font-semibold text-2xl md:text-3xl mb-3 text-balance">OCFS license active</h2>
                            <p className="text-soilmuted text-lg mb-2">
                                We are a group family daycare program under the New York State Office of Children and Family
                                Services (OCFS).
                            </p>
                            <p className="text-sm text-soil/60">
                                Our license is fully active and on file with OCFS.{" "}
                                <a
                                    href="https://ocfs.ny.gov/CCFS/Lookup"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="safety-inspection-history-link"
                                    className="text-sageink underline underline-offset-4"
                                >
                                    View our inspection history
                                </a>{" "}
                                through the official NYS Child Care Search.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Highlights */}
            <section className="pb-16 md:pb-20" aria-labelledby="highlights-heading">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <h2 id="highlights-heading" className="font-display font-semibold text-2xl md:text-3xl mb-8 md:mb-10">
                            How we keep every child safe
                            <WavyUnderline className="block w-44 h-2.5 mt-2" color="#E07A5F" />
                        </h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {HIGHLIGHTS.map((h, i) => (
                            <Reveal key={h.title} delay={i * 0.06} className="h-full">
                                <div className="hover-lift bg-creamsoft border-2 border-soil/10 rounded-3xl p-6 h-full" data-testid={`safety-card-${i + 1}`}>
                                    <span className="inline-flex p-3 rounded-2xl bg-white border-2 border-soil/10 mb-4 text-sageink">
                                        <h.icon size={24} />
                                    </span>
                                    <h3 className="font-display font-semibold text-lg mb-2">{h.title}</h3>
                                    <p className="text-soilmuted text-sm leading-relaxed">{h.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Sick policy */}
            <section className="pb-16 md:pb-24">
                <div className="max-w-4xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <div className="bg-skysoft border-2 border-sky rounded-[2rem] p-7 md:p-9" data-testid="safety-sick-policy-card">
                            <div className="flex items-start gap-4">
                                <span className="inline-flex p-3 rounded-2xl bg-white shrink-0">
                                    <Thermometer size={24} className="text-coraldeep" />
                                </span>
                                <div>
                                    <h2 className="font-display font-semibold text-xl md:text-2xl mb-3">When should a child stay home?</h2>
                                    <p className="text-soilmuted leading-relaxed mb-4">
                                        Keep your child home for a fever of 100.4°F or higher with symptoms, vomiting or diarrhea
                                        twice in 24 hours, or a rash that hasn't been checked by a doctor. Children can return after
                                        24 hours symptom-free.
                                    </p>
                                    <ProtectedDownload
                                        file="parent-handbook.pdf"
                                        label="Read the full sick-child policy in the Parent Handbook"
                                        testId="safety-full-policy-link"
                                        className="!rounded-2xl !normal-case underline underline-offset-4"
                                    />
                                </div>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-8 text-center text-sm text-soil/55" data-testid="safety-small-print">
                            OCFS regulations (Part 416) are available at{" "}
                            <a href={OCFS_REGS} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 font-bold">
                                ocfs.ny.gov
                            </a>{" "}
                            or at our program on request.
                        </p>
                    </Reveal>
                </div>
            </section>

            <CTABand title="See our safety practices up close." note="Tours fill quickly — spots are limited." />
        </div>
    );
}

const ShieldCheckBig = () => (
    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#5E8355" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
        <path d="m9 12 2 2 4-4" />
    </svg>
);
